import { z } from 'zod/v3';
import { DocumentModel } from './document.model';
import { GraphModel } from '../graph/graph.model';
import {
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../actions/framework';
import { Assert } from '@cotera/utilities';
import { ActionResult, AskResponse } from '../actions/framework/types';
import { useState } from 'react';
import { toast, Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { err, ok } from 'neverthrow';
import { GRAPH_ACTION_GROUP } from '../dataset/actions/constants';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Routes } from '../routes';

type Params = {
  to: string;
};

export class RenameDocumentAction extends BaseAction<Params, { t: string }[]> {
  title = 'Rename Document';
  description = 'Renames a document and all its references within the graph';
  inputSchema = z.object({
    to: z.string(),
  });
  group = GRAPH_ACTION_GROUP;
  icon = 'pencil';

  constructor(
    private readonly navigator: {
      pushAndReplace: (props: { title: string; route: string }) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const documentModel = context.targetedModelOfType(DocumentModel);

    if (documentModel === null) {
      return false;
    }

    return documentModel.currentNameInGraph.snapshot() !== null;
  }

  async execute(
    input: z.infer<typeof this.inputSchema>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    const document = context.targetedModelOfType(DocumentModel);

    Assert.assert(document !== null, 'No targeted document');
    Assert.assert(graph !== null);

    document.formState.set({
      ...document.formState.snapshot(),
      documentState: document.editorState
        .snapshot()
        .setTitle(input.to)
        .getState(),
    });

    const result = document.compile();

    if (result.isErr()) {
      const error = {
        t: result.error.t,
        message: JSON.stringify(result.error),
      };
      toast.error(error.message);
      return err([error]);
    }

    const stagingResult = await graph.attemptToStageOperations(
      document.newOperations.snapshot()
    );

    document.markAsStaged();

    if (stagingResult.isErr()) {
      const error = {
        t: stagingResult.error.err.t,
        message: JSON.stringify(stagingResult.error.err),
      };
      return err([error]);
    }

    this.navigator.pushAndReplace({
      title: document.name.snapshot()!,
      route: Routes.document.forId(document.name.snapshot()!).toString(),
    });

    return ok({});
  }

  ask(_: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    const targetedDocument = context.targetedModelOfType(DocumentModel);
    Assert.assert(targetedDocument !== null);

    const documentName = targetedDocument.currentNameInGraph.snapshot();
    Assert.assert(documentName !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                name={documentName}
                description={this.description}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  name: string;
  description: string;
}> = ({ onRespond, name, description }) => {
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView
      title={`Rename ${name}`}
      subtitle={description}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <Input
          autoFocus
          placeholder="name"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              await onRespond({ to });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
